import { cn } from "../lib/cn";

/** Column alignment. Numbers and timestamps read right, everything else left. */
export type LedgerAlign = "left" | "center" | "right";

const alignClasses: Record<LedgerAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export interface LedgerTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Merged onto the paper wrapper, not the inner `<table>`. */
  className?: string;
  ref?: React.Ref<HTMLTableElement>;
}

/**
 * Green-bar continuous-feed ledger paper: banded rows built for scanning long
 * columns without losing your line. This is the audit trail - what happened, in
 * order, permanently. Compose with LedgerHead / LedgerHeadCell / LedgerBody /
 * LedgerRow / LedgerCell.
 */
export function LedgerTable({
  children,
  className,
  ref,
  ...rest
}: LedgerTableProps) {
  return (
    <div
      className={cn(
        "overflow-x-auto bg-card border border-rule rounded-paper",
        className,
      )}
    >
      <table ref={ref} className="w-full border-collapse" {...rest}>
        {children}
      </table>
    </div>
  );
}

export interface LedgerHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  ref?: React.Ref<HTMLTableSectionElement>;
}

/** The column headings strip at the top of the ledger sheet. Holds LedgerHeadCell. */
export function LedgerHead({
  children,
  className,
  ref,
  ...rest
}: LedgerHeadProps) {
  return (
    <thead ref={ref} className={cn(className)} {...rest}>
      {children}
    </thead>
  );
}

export interface LedgerHeadCellProps extends Omit<
  React.ThHTMLAttributes<HTMLTableCellElement>,
  "align"
> {
  align?: LedgerAlign;
  ref?: React.Ref<HTMLTableCellElement>;
}

/** One column heading: mono small-caps annotation over the heaviest rule on the sheet. */
export function LedgerHeadCell({
  align = "left",
  children,
  className,
  ref,
  ...rest
}: LedgerHeadCellProps) {
  return (
    <th
      ref={ref}
      scope="col"
      className={cn(
        "text-annotation-caps text-annotation px-3 py-2 border-b border-rule",
        alignClasses[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface LedgerBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  ref?: React.Ref<HTMLTableSectionElement>;
}

/** The body of the sheet: the entries themselves, oldest or newest first as the caller decides. */
export function LedgerBody({
  children,
  className,
  ref,
  ...rest
}: LedgerBodyProps) {
  return (
    <tbody ref={ref} className={cn(className)} {...rest}>
      {children}
    </tbody>
  );
}

export interface LedgerRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  ref?: React.Ref<HTMLTableRowElement>;
}

/** One entry. Every other row carries the faint green bar that keeps your eye on the line. */
export function LedgerRow({
  children,
  className,
  ref,
  ...rest
}: LedgerRowProps) {
  return (
    <tr
      ref={ref}
      className={cn(
        "even:bg-[color-mix(in_srgb,var(--color-wash-working)_16%,transparent)]",
        // Row hairlines stay lighter than the head rule.
        "border-b border-rule/50",
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export interface LedgerCellProps extends Omit<
  React.TdHTMLAttributes<HTMLTableCellElement>,
  "align"
> {
  /** Tabular data (times, ids, amounts) sets in the mono face. */
  mono?: boolean;
  align?: LedgerAlign;
  ref?: React.Ref<HTMLTableCellElement>;
}

/** One field in an entry. */
export function LedgerCell({
  mono = false,
  align = "left",
  children,
  className,
  ref,
  ...rest
}: LedgerCellProps) {
  return (
    <td
      ref={ref}
      className={cn(
        "px-3 py-2 text-sm text-ink-body",
        mono && "font-mono text-[0.8125rem]",
        alignClasses[align],
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
